import MenuIcon from '@mui/icons-material/Menu';
import { Divider, IconButton, Link, Menu, Tab, Tabs } from '@mui/material';
import AppBar, { AppBarProps } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import { common, grey } from '@mui/material/colors';
import React, { useMemo, useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';
import { Link as RouterLink, matchPath, useLocation, useMatch, useNavigate } from 'react-router-dom';

import { useHydrophoneDevices } from '@/api/device/useHydrophoneDevices';
import { useDeviceIdList } from '@/api/deviceIdList';
import { useDevices } from '@/api/devices';
import { useIsAdmin, useLogout } from '@/api/userAccount';
import logo from '@/assets/sofar-logo-white.png';
import { BetaChip } from '@/components/BetaChip';
import { WavelinkStatusChip } from '@/components/WavelinkStatusChip';
import { ChatNavButton } from '@/components/chat/ChatNavButton';
import { InlineAlert } from '@/components/ui/InlineAlert';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useLoginRedirectUrl } from '@/hooks/useLoginRedirectUrl';
import { useGlobalStore } from '@/store';
import { blue, boldWeight } from '@/theme';

type NavLink = {
    label: string;
    path: string;
    asRegularLink?: boolean;
    badge?: React.ReactElement;
};

const MAP_LINKS = {
    spottersMap: { label: 'Spotter Map', path: '/' },
    acousticIntelligence: { label: 'Acoustics Intelligence', path: '/acoustics', badge: <BetaChip /> },
} as const satisfies Record<string, NavLink>;

export const TopNavBar = ({ sx, ...props }: AppBarProps) => {
    const loginUrl = useLoginRedirectUrl();
    const navigate = useNavigate();
    const isAdmin = useIsAdmin();
    const isLoggedIn = useGlobalStore((state) => state.isLoggedIn);
    const setIsLoggedIn = useGlobalStore((state) => state.setIsLoggedIn);
    const { refetch: logout } = useLogout({ config: { enabled: false } });
    const mixpanel = useMixpanel();
    const acousticIntelligenceView = useFeatureFlag('acousticIntelligenceView');
    // no need to check for hydrophone devices if acoustic intelligence view is enabled via feature flag
    const isHydrophoneCheckEnabled = isLoggedIn && !acousticIntelligenceView;
    const { data: deviceIdItems } = useDeviceIdList({ config: { enabled: isHydrophoneCheckEnabled } });
    const { data: devices = [] } = useDevices({
        devices: isHydrophoneCheckEnabled ? (deviceIdItems?.data?.devices ?? []) : [],
        config: { enabled: isHydrophoneCheckEnabled },
    });
    const hydrophoneDevices = useHydrophoneDevices(devices, { enabled: isHydrophoneCheckEnabled });
    const hasHydrophoneDevices = hydrophoneDevices.length > 0;

    const topLevelLinks: NavLink[] = useMemo(() => {
        if (hasHydrophoneDevices || acousticIntelligenceView) {
            return [MAP_LINKS.spottersMap, MAP_LINKS.acousticIntelligence];
        }
        return [MAP_LINKS.spottersMap];
    }, [hasHydrophoneDevices, acousticIntelligenceView]);

    // Nav menu
    const [navMenuAnchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleCloseNavMenu = () => {
        setAnchorEl(null);
    };
    const handleLogout = async () => {
        await logout();
        setIsLoggedIn(false);
        mixpanel?.track('Logout');
        mixpanel?.reset();
        handleCloseNavMenu();
        navigate('/login');
    };

    return (
        <AppBar
            enableColorOnDark
            position="static"
            elevation={0}
            sx={{
                top: 0,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: blue[700],
                ...sx,
            }}
            {...props}
        >
            <Toolbar
                sx={{
                    flexWrap: 'wrap',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    alignItems: 'center',
                }}
            >
                <Box sx={{ justifySelf: 'start' }}>
                    <RouterLink to="/">
                        <Box sx={{ maxHeight: 35 }} component="img" src={logo} alt="sofar ocean logo" />
                    </RouterLink>
                </Box>

                <TopNavTabs links={topLevelLinks} />

                <Box sx={{ justifySelf: 'end', display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isAdmin && (
                        <InlineAlert
                            severity="warning"
                            size="small"
                            sx={{
                                py: 0,
                                px: 1,
                                width: '15em',
                                color: 'warning.main',
                                fontWeight: boldWeight,
                                borderWidth: '2px',
                                flexWrap: 'nowrap',
                            }}
                        >
                            Administrator account
                        </InlineAlert>
                    )}
                    <WavelinkStatusChip />
                    {isLoggedIn ? (
                        <>
                            <ChatNavButton />
                            <Box>
                                {/* Nav menu */}
                                <IconButton
                                    size="large"
                                    aria-label="page navigation"
                                    aria-controls="navigation-menu"
                                    aria-haspopup="true"
                                    onClick={handleOpenNavMenu}
                                    color="inherit"
                                >
                                    <MenuIcon />
                                </IconButton>
                                <Menu
                                    id="navigation-menu"
                                    anchorEl={navMenuAnchorEl}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    keepMounted
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                borderRadius: 0,
                                            },
                                        },
                                    }}
                                    open={Boolean(navMenuAnchorEl)}
                                    onClose={handleCloseNavMenu}
                                >
                                    <Box
                                        sx={{
                                            /* break points must mirror topLevelLinks Box at top level above */
                                            display: { xs: 'initial', md: 'none' },
                                        }}
                                    >
                                        {topLevelLinks.map((page) => {
                                            return (
                                                <LinkMenuItem
                                                    key={page.label}
                                                    to={page.path}
                                                    asRegularLink={page.asRegularLink}
                                                    onClick={handleCloseNavMenu}
                                                >
                                                    <NavLinkLabel {...page} />
                                                </LinkMenuItem>
                                            );
                                        })}
                                        <Divider />
                                    </Box>
                                    <LinkMenuItem onClick={handleCloseNavMenu} to="/setup/register">
                                        Register Spotter
                                    </LinkMenuItem>
                                    <Divider />
                                    <LinkMenuItem onClick={handleCloseNavMenu} to="/account">
                                        Account
                                    </LinkMenuItem>
                                    <LinkMenuItem onClick={handleCloseNavMenu} to="/telemetry">
                                        Telemetry
                                    </LinkMenuItem>
                                    <LinkMenuItem onClick={handleCloseNavMenu} to="/billing">
                                        Billing History
                                    </LinkMenuItem>
                                    <LinkMenuItem onClick={handleCloseNavMenu} to="/payment">
                                        Payment Settings
                                    </LinkMenuItem>
                                    <LinkMenuItem onClick={handleCloseNavMenu} to="/api">
                                        Sofar API
                                    </LinkMenuItem>
                                    <Divider />
                                    <LinkMenuItem onClick={handleLogout} to="#" color="error.main">
                                        Logout
                                    </LinkMenuItem>
                                </Menu>
                            </Box>
                        </>
                    ) : (
                        <TopNavLink to={loginUrl} asRegularLink>
                            Login
                        </TopNavLink>
                    )}
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export interface TopNavLinkProps {
    to: string;
    children?: React.ReactNode;
    asRegularLink?: boolean;
}

export const TopNavLink = ({ to = '/', children, asRegularLink = false }: TopNavLinkProps) => {
    return (
        <Link
            variant="button"
            to={to}
            href={to}
            sx={{
                my: 1,
                mx: 1.5,
                color: grey[400],
                '&:hover': { color: common.white },
            }}
            component={asRegularLink ? 'a' : RouterLink}
            underline="hover"
        >
            {children}
        </Link>
    );
};

const NavLinkLabel = ({ label, badge }: NavLink): React.ReactElement => (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <Box component="span" className="nav-link-text" sx={{ position: 'relative', alignSelf: 'stretch' }}>
            {label}
        </Box>
        {badge}
    </Box>
);

export const TopNavTabs = ({ links }: { links: NavLink[] }) => {
    const location = useLocation();
    const activeTopLinkIndex = links?.findIndex((tab) => matchPath({ path: tab.path, end: true }, location.pathname));

    return (
        <Box component="nav" sx={{ justifySelf: 'center' }}>
            <Tabs
                value={activeTopLinkIndex < 0 ? false : activeTopLinkIndex}
                aria-label="map view tabs"
                sx={{
                    /* break points must mirror topLevelLinks Box within Menu below */
                    display: { xs: 'none', md: 'flex' },
                    minHeight: 0,
                    '& .MuiTabs-indicator': { display: 'none' },
                    '& .MuiTab-root': {
                        color: common.white,
                        opacity: 0.7,
                        minHeight: 0,
                        minWidth: 0,
                        px: 0,
                        py: 1,
                        mx: 1.5,
                    },
                    '& .MuiTab-root:hover': { opacity: 1 },
                    '& .MuiTab-root.Mui-selected': { color: common.white, opacity: 1 },
                    '& .MuiTab-root.Mui-selected:hover': { cursor: 'default' },
                    '& .MuiTab-root.Mui-selected .nav-link-text::after': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: (theme) => theme.spacing(-1),
                        height: 2,
                        backgroundColor: common.white,
                    },
                }}
            >
                {links.map((tab) => (
                    <Tab
                        key={tab.path}
                        label={<NavLinkLabel {...tab} />}
                        component={RouterLink}
                        disableRipple
                        to={tab.path}
                    />
                ))}
            </Tabs>
        </Box>
    );
};

interface LinkMenuItemProps {
    to: string;
    children?: React.ReactNode;
    onClick?: () => void;
    color?: string;
    asRegularLink?: boolean;
}

export const LinkMenuItem = ({ to = '/', children, onClick, color, asRegularLink = false }: LinkMenuItemProps) => {
    const pathMatch = useMatch({ path: `${to}/*`, end: true });

    return (
        <MenuItem
            to={to}
            href={to}
            selected={!!pathMatch && !asRegularLink}
            onClick={onClick}
            sx={{
                px: 2,
                color,
            }}
            component={asRegularLink ? 'a' : RouterLink}
        >
            {children}
        </MenuItem>
    );
};
